/**
 * SamvadContentEditor — Book drafting tool for Upadhyaya and above.
 * Sprint 5: Allows drafting a Sanskrit conversation book (title, description,
 * archetype, phase, proficiency target, content in Markdown) and submitting
 * it for Acharya review.
 *
 * Domain rules:
 *   - Book status transitions: draft → submitted → approved | rejected
 *   - Only the Acharya can approve or reject (never automated here)
 *   - Status badges: मसौदा / समीक्षाधीन / प्रकाशित / अस्वीकृत
 */
import { useState, useEffect } from 'react';
import '../styles/SamvadContentEditor.css';

interface Props {
  onNavigate: (path: string) => void;
}

interface Book {
  id: string;
  title_hi: string;
  title_en: string;
  description: string;
  archetype: string;
  phase: number;
  target_proficiency: number;
  content: string;
  author_id: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
  reviewed_by: string | null;
  review_notes: string;
}

const STATUS_LABELS: Record<string, { hi: string; cls: string }> = {
  draft:     { hi: 'मसौदा',        cls: 'sce-s-draft'     },
  submitted: { hi: 'समीक्षाधीन',   cls: 'sce-s-submitted' },
  approved:  { hi: 'प्रकाशित',      cls: 'sce-s-approved'  },
  rejected:  { hi: 'अस्वीकृत',     cls: 'sce-s-rejected'  },
};

const ARCHETYPE_OPTIONS = [
  { value: 'BRAHMIN',   label: 'ब्राह्मण — Knowledge & Teaching' },
  { value: 'KSHATRIYA', label: 'क्षत्रिय — Leadership & Protection' },
  { value: 'VAISHYA',   label: 'वैश्य — Exchange & Sustenance' },
  { value: 'SHUDRA',    label: 'शूद्र — Craft & Refined Service' },
];

const PHASE_OPTIONS = [
  { value: 1, label: '॥ १ ॥ — शिक्षार्थी प्रवेश (Enrollment)' },
  { value: 2, label: '॥ २ ॥ — पाठ्यक्रम निर्माण (Curriculum)' },
  { value: 3, label: '॥ ३ ॥ — छात्र युगलन (Pairing)' },
  { value: 4, label: '॥ ४ ॥ — अभ्यास सत्र (Practice)' },
  { value: 5, label: '॥ ५ ॥ — व्याकरण-निरुक्ति (Grammar)' },
  { value: 6, label: '॥ ६ ॥ — मूल्यांकन (Assessment)' },
  { value: 7, label: '॥ ७ ॥ — उपाधि प्रदान (Title Granting)' },
  { value: 8, label: '॥ ८ ॥ — वरिष्ठ-शिष्य उन्नयन (Elevation)' },
];

const PROF_OPTIONS = [
  { value: 1, label: '१ — प्रारम्भिक (Beginner)' },
  { value: 2, label: '२ — मूल (Basic)' },
  { value: 3, label: '३ — मध्यम (Intermediate)' },
  { value: 4, label: '४ — उन्नत (Advanced)' },
  { value: 5, label: '५ — प्रवाही (Fluent)' },
];

const CONTENT_PLACEHOLDER = `<!-- संवाद-प्रारूप (Markdown) -->

## दृश्य १ — प्रथम भेंट

**राम:** आप कहाँ से हैं?
*rāma: āpaḥ kutaḥ santi?*

**श्याम:** मैं दिल्ली से हूँ।
*śyāma: ahaṃ dihlī-nagarāt asmi.*

---

**व्याकरण-टिप्पणी (Grammar Notes)**
- **कहाँ से** → *kutaḥ* (पञ्चमी विभक्ति — ablative)
- **हूँ** → *asmi* (लट् लकार, उत्तम पुरुष, एकवचन)
`;

export default function SamvadContentEditor({ onNavigate }: Props) {
  const authorId = localStorage.getItem('samvad_participant_id') ?? '';

  // ── My drafts list ──
  const [myBooks, setMyBooks]   = useState<Book[]>([]);
  const [loadingBooks, setLoadingBooks] = useState(true);

  // ── New book form ──
  const [showForm, setShowForm]     = useState(false);
  const [titleHi, setTitleHi]       = useState('');
  const [titleEn, setTitleEn]       = useState('');
  const [description, setDescription] = useState('');
  const [archetype, setArchetype]   = useState('BRAHMIN');
  const [phase, setPhase]           = useState(2);
  const [targetProf, setTargetProf] = useState(2);
  const [content, setContent]       = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving]         = useState(false);
  const [saveResult, setSaveResult] = useState<string | null>(null);

  // ── Submit for review ──
  const [submitting, setSubmitting] = useState<string | null>(null);

  function loadMyBooks() {
    if (!authorId) { setLoadingBooks(false); return; }
    setLoadingBooks(true);
    fetch(`/api/samvad/books?author_id=${encodeURIComponent(authorId)}`)
      .then(r => r.json())
      .then(d => { setMyBooks(d.books ?? []); setLoadingBooks(false); })
      .catch(() => setLoadingBooks(false));
  }

  useEffect(() => { loadMyBooks(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function validateForm() {
    const errs: Record<string, string> = {};
    if (!titleHi.trim()) errs.titleHi = 'हिंदी शीर्षक आवश्यक है';
    if (!content.trim() || content.trim().length < 50) errs.content = 'संवाद-सामग्री कम से कम 50 अक्षर होनी चाहिए';
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSaveDraft() {
    if (!validateForm()) return;
    setSaving(true);
    setSaveResult(null);
    try {
      const res = await fetch('/api/samvad/books', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          author_id:          authorId,
          title_hi:           titleHi.trim(),
          title_en:           titleEn.trim(),
          description:        description.trim(),
          archetype,
          phase,
          target_proficiency: targetProf,
          content:            content,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setSaveResult(`❌ ${data.detail ?? 'सहेजने में त्रुटि'}`); return; }
      setSaveResult(`✅ मसौदा सहेजा: ${data.book_id}`);
      setShowForm(false);
      setTitleHi(''); setTitleEn(''); setDescription(''); setContent('');
      loadMyBooks();
    } catch {
      setSaveResult('❌ नेटवर्क त्रुटि — पुनः प्रयास करें');
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmitForReview(bookId: string) {
    setSubmitting(bookId);
    try {
      const res = await fetch(`/api/samvad/books/${bookId}/status`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewer_id: authorId, status: 'submitted', review_notes: '' }),
      });
      if (res.ok) loadMyBooks();
    } finally {
      setSubmitting(null);
    }
  }

  return (
    <div className="sce-root">
      {/* Header */}
      <div className="sce-header">
        <button className="sce-back-btn" onClick={() => onNavigate('/samvad/dashboard')}>
          ← डैशबोर्ड
        </button>
        <div>
          <h1 className="sce-title">📖 संवाद-पुस्तक लेखन</h1>
          <p className="sce-sub">Conversation Book Drafting Tool · Upadhyaya & above</p>
        </div>
      </div>

      {/* Author guard */}
      {!authorId && (
        <div className="sce-warn">
          ⚠ इस टूल का उपयोग करने के लिए पहले पंजीकरण करें।{' '}
          <button className="sce-link" onClick={() => onNavigate('/samvad/onboarding')}>
            पंजीकरण करें →
          </button>
        </div>
      )}

      {/* My drafts list */}
      <section className="sce-section">
        <div className="sce-section-head">
          <h2 className="sce-section-title">मेरी पुस्तकें · My Books</h2>
          {authorId && (
            <button className="sce-btn-primary" onClick={() => setShowForm(s => !s)}>
              {showForm ? '✕ रद्द करें' : '＋ नई पुस्तक'}
            </button>
          )}
        </div>

        {saveResult && <div className="sce-result">{saveResult}</div>}

        {/* New book form */}
        {showForm && (
          <div className="sce-form-card">
            <h3 className="sce-form-title">नई संवाद-पुस्तक का मसौदा</h3>

            <div className="sce-field">
              <label className="sce-label">हिंदी शीर्षक <span className="sce-req">*</span></label>
              <input className={`sce-input ${formErrors.titleHi ? 'sce-input--err' : ''}`}
                value={titleHi} onChange={e => setTitleHi(e.target.value)}
                placeholder="जैसे: गोशाला-संवाद" />
              {formErrors.titleHi && <span className="sce-err">{formErrors.titleHi}</span>}
            </div>

            <div className="sce-field">
              <label className="sce-label">English Title (optional)</label>
              <input className="sce-input" value={titleEn}
                onChange={e => setTitleEn(e.target.value)}
                placeholder="e.g. Cow-shed Dialogue" />
            </div>

            <div className="sce-field">
              <label className="sce-label">संक्षिप्त विवरण · Description (optional)</label>
              <textarea className="sce-textarea sce-textarea--sm" rows={2}
                value={description} onChange={e => setDescription(e.target.value)}
                placeholder="इस पुस्तक का विषय और उद्देश्य…" />
            </div>

            <div className="sce-row-3">
              <div className="sce-field">
                <label className="sce-label">Archetype</label>
                <select className="sce-select" value={archetype}
                  onChange={e => setArchetype(e.target.value)}>
                  {ARCHETYPE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="sce-field">
                <label className="sce-label">चरण · Phase</label>
                <select className="sce-select" value={phase}
                  onChange={e => setPhase(Number(e.target.value))}>
                  {PHASE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
              <div className="sce-field">
                <label className="sce-label">लक्ष्य स्तर · Target Level</label>
                <select className="sce-select" value={targetProf}
                  onChange={e => setTargetProf(Number(e.target.value))}>
                  {PROF_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="sce-field">
              <label className="sce-label">
                संवाद-सामग्री · Dialogue Content (Markdown)
                <span className="sce-req"> *</span>
              </label>
              <p className="sce-hint">
                Markdown में लिखें — दृश्य (##), वार्तालाप (**वक्ता:**), व्याकरण-टिप्पणी।
              </p>
              <textarea
                className={`sce-textarea sce-textarea--lg ${formErrors.content ? 'sce-input--err' : ''}`}
                rows={18}
                value={content}
                onChange={e => setContent(e.target.value)}
                placeholder={CONTENT_PLACEHOLDER}
                spellCheck={false}
              />
              {formErrors.content && <span className="sce-err">{formErrors.content}</span>}
              <span className="sce-char-count">{content.length} अक्षर</span>
            </div>

            <div className="sce-form-actions">
              <button className="sce-btn-secondary" onClick={() => setShowForm(false)}>
                रद्द करें
              </button>
              <button className="sce-btn-primary" onClick={handleSaveDraft} disabled={saving}>
                {saving ? '💾 सहेज रहे हैं…' : '💾 मसौदा सहेजें'}
              </button>
            </div>
          </div>
        )}

        {/* Draft list */}
        {loadingBooks ? (
          <p className="sce-loading">पुस्तकें लोड हो रही हैं…</p>
        ) : myBooks.length === 0 ? (
          <div className="sce-empty">
            <p>अभी कोई पुस्तक नहीं। ऊपर "＋ नई पुस्तक" से शुरू करें।</p>
          </div>
        ) : (
          <ul className="sce-book-list">
            {myBooks.map(b => {
              const sl = STATUS_LABELS[b.status] ?? { hi: b.status, cls: '' };
              return (
                <li key={b.id} className="sce-book-row">
                  <div className="sce-book-info">
                    <span className="sce-book-title">{b.title_hi}</span>
                    {b.title_en && <span className="sce-book-en">{b.title_en}</span>}
                    <div className="sce-book-meta">
                      <span className="sce-meta-chip">चरण {b.phase}</span>
                      <span className="sce-meta-chip">स्तर {b.target_proficiency}</span>
                      <span className="sce-meta-chip">{b.archetype}</span>
                      <span className="sce-meta-id">{b.id}</span>
                    </div>
                    {b.review_notes && (
                      <p className="sce-review-notes">
                        💬 आचार्य टिप्पणी: {b.review_notes}
                      </p>
                    )}
                  </div>
                  <div className="sce-book-actions">
                    <span className={`sce-status ${sl.cls}`}>{sl.hi}</span>
                    {b.status === 'draft' && (
                      <button
                        className="sce-btn-submit"
                        onClick={() => handleSubmitForReview(b.id)}
                        disabled={submitting === b.id}
                      >
                        {submitting === b.id ? '⏳' : '📤 समीक्षा हेतु भेजें'}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Guidelines */}
      <section className="sce-section sce-guidelines">
        <h2 className="sce-section-title">📋 लेखन-दिशानिर्देश · Writing Guidelines</h2>
        <ul className="sce-guide-list">
          <li>संवाद में प्रत्येक वाक्य ≤ 9 शब्द (Sanskrit ≤ 9 words per utterance)</li>
          <li>प्रत्येक दृश्य में कम से कम एक कारक-विश्लेषण (at least one kāraka analysis per scene)</li>
          <li>Hindi meaning: तत्सम शब्द only — Urdu/Arabic/Persian loanwords avoid करें</li>
          <li>व्याकरण-टिप्पणी में धातु + निरुक्त + अर्थ शामिल करें</li>
          <li>लक्ष्य स्तर के अनुसार जटिलता रखें — बीज (1–2) से फल (5) तक</li>
        </ul>
      </section>
    </div>
  );
}
